import React, { useContext, useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useNode, useEditor } from '@craftjs/core';

import { BORDER_OPTIONS, EDITOR_RESOLVERS } from '@common/constants';
import SettingsWrapper from '@common/components/SettingsWrapper/SettingsWrapper';
import SettingsRow from '@common/components/SettingsWrapper/SettingsRow';
import { Typography } from '@mui/material';
import MyButton from '@common/components/Button/MyButton';
import TableModal from '@resolvers/common/Table/components/TableModal';
import SizeStyle from '@resolvers/tools/SizeStyle/SizeStyle';
import PaddingStyle from '@resolvers/tools/PaddingStyle/PaddingStyle';
import useParentSize from '@resolvers/tools/useParentSize/useParentSize';
import {
  calcNetNodeSize,
  getFlex,
} from '@common/helpers/domManipulationHelpers';
import BorderStyle from '@resolvers/tools/BorderStyle/BorderStyle';
import FlexAlignment from '@resolvers/tools/FlexAlignment/FlexAlignment';
import SizeStyleDivide from '@resolvers/tools/SizeStyle/SizeStyleDivide';
import SelectColor from '@resolvers/tools/SelectColor/SelectColor';
import { useTemplateDetailsStore } from '@store/templateDetailsStore';

const Cards = ({
  children,
  cardSrc,
  height,
  width,
  borderColor,
  borderLeft,
  borderBottom,
  borderTop,
  borderRight,
  flexDirection,
  flexOrder,
  justifyContent,
  alignItems,
  paddingVertical,
  paddingHorizontal,
  background,
}) => {
  const { actions, query, selected } = useEditor((state, query) => ({
    selected: state.events.selected,
  }));

  const {
    connectors: { connect, drag },
    actions: { setCustom },
    id,
  } = useNode();

  const { t } = useTranslation();

  const ref = useRef(null);

  useEffect(() => {
    if (!id) return;

    if (!width) {
      const { width, height } = calcNetNodeSize(query);

      const cardWidth = Math.floor(width / 3);
      const cardHeight = Math.floor(height / 3);

      actions.setProp(id, (props) => {
        props.width = cardWidth;
        props.height = cardHeight;
      });
    }
  }, [selected]);

  useEffect(() => {
    if (ref?.current && connect) {
      connect(drag(ref.current));
    }
  }, [connect]);

  useEffect(() => {
    actions.history.ignore(() => {
      setCustom((props) => {
        props.displayName = t('cards');
      });
    });
  }, [setCustom, t]);

  const flexValues = getFlex({ flexDirection, flexOrder });

  return (
    <div
      className='cards'
      style={{
        backgroundColor: background,
        height: `${height}px`,
        width: `${width}px`,
        paddingLeft: `${paddingHorizontal || 0}px`,
        paddingRight: `${paddingHorizontal || 0}px`,
        paddingTop: `${paddingVertical || 0}px`,
        paddingBottom: `${paddingVertical || 0}px`,
        boxSizing: 'border-box',
        borderLeft: `${borderLeft ? `1px solid ${borderColor}` : ''}`,
        borderBottom: `${borderBottom ? `1px solid ${borderColor}` : ''}`,
        borderTop: `${borderTop ? `1px solid ${borderColor}` : ''}`,
        borderRight: `${borderRight ? `1px solid ${borderColor}` : ''}`,
        rowGap: 0,
        columnGap: 0,
        gap: 0,
        pageBreakInside: 'avoid',
        breakInside: 'avoid',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems,
        justifyContent,
        ...flexValues,
      }}
      ref={ref}
      data-hbs-cards-each={cardSrc}
    >
      {children}
    </div>
  );
};

const CardsConfigPanel = () => {
  const { source } = useTemplateDetailsStore();
  const { t } = useTranslation();

  const {
    actions: { setProp },
    cardSrc,
  } = useNode(({ data: { props } }) => ({
    cardSrc: props.cardSrc,
  }));

  const [showModal, setShowModal] = useState({ show: false });
  const parentSize = useParentSize();

  const onSelectSourceClick = (e) => {
    setShowModal({ show: true });
  };

  const changeSrcValue = (newValue) => {
    setProp((props) => {
      props.cardSrc = newValue;
    }, 500);
  };

  return (
    <SettingsWrapper>
      <TableModal
        currentValue={cardSrc}
        documentJson={source?.structure || {}}
        showModal={showModal}
        setShowModal={setShowModal}
        changeSrcValue={changeSrcValue}
      />
      <SettingsRow sx={{ mb: 1 }}>
        <Typography>{`${t('property_name')}:`}</Typography>
        <Typography>{cardSrc}</Typography>
      </SettingsRow>
      <SettingsRow sx={{ mb: 3 }}>
        <MyButton onClick={onSelectSourceClick}>{t('select_source')}</MyButton>
      </SettingsRow>
      <SettingsRow>
        <SizeStyle
          propKey='height'
          label={t('height')}
          max={parentSize.height}
          blockOutboundValues={true}
        />
        <SizeStyleDivide propKey={'height'} />
      </SettingsRow>
      <SettingsRow>
        <SizeStyle
          propKey='width'
          label={t('width')}
          max={parentSize.width}
          blockOutboundValues={true}
        />
        <SizeStyleDivide propKey={'width'} />
      </SettingsRow>
      <PaddingStyle showVH={true} />
      <FlexAlignment />
      <SettingsRow sx={{ mb: 3 }}>
        <BorderStyle options={BORDER_OPTIONS} />
      </SettingsRow>
      <SettingsRow>
        <SelectColor />
      </SettingsRow>
    </SettingsWrapper>
  );
};

export const CardsDefaultProps = {
  children: null,
  cardSrc: undefined,
  height: undefined,
  width: undefined,
  fontSize: '12',
  paddingHorizontal: '5',
  paddingVertical: '5',
  borderColor: '#e0e1e2',
  borderAllShow: true,
  borderLeft: false,
  borderBottom: false,
  borderTop: false,
  borderRight: false,
  flexDirection: 'column',
  justifyContent: 'flex-start',
  flexOrder: 'normal',
  alignItems: 'flex-start',
  background: '#ffffff',
  borderColor: '#F6EDED',
  isDeletable: true,
};

Cards.propTypes = {
  background: PropTypes.string,
  borderColor: PropTypes.string,
  children: PropTypes.node,
  paddingHorizontal: PropTypes.string,
  paddingVertical: PropTypes.string,
  height: PropTypes.string,
  width: PropTypes.string,
  borderLeft: PropTypes.bool,
  borderBottom: PropTypes.bool,
  borderTop: PropTypes.bool,
  borderRight: PropTypes.bool,
  flexDirection: PropTypes.string,
  flexOrder: PropTypes.string,
  justifyContent: PropTypes.string,
  alignItems: PropTypes.string,
};

Cards.craft = {
  displayName: EDITOR_RESOLVERS.PDF.CARDS,
  name: EDITOR_RESOLVERS.PDF.CARDS,
  props: CardsDefaultProps,
  related: { settings: CardsConfigPanel },
  rules: {
    canDrop: (targetNode) => {
      return (
        targetNode.data.name === EDITOR_RESOLVERS.PDF.PAGE_BODY ||
        targetNode.data.name === EDITOR_RESOLVERS.PDF.CONTAINER
      );
    },
    canMoveIn: (node) => {
      return node[0]?.data?.name !== EDITOR_RESOLVERS.COMMON.TABLE;
    },
  },
};

export default Cards;
